import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as messagingApi from '@/api/messaging.api';
import { mutationMeta } from '@/lib/query-client';
import type { Message } from '@/types';
import type { MailComposePayload, MailboxFolder } from '@/pages/messages/mailbox.types';

export function useMessages(params?: { page?: number; per_page?: number; folder?: string }) {
  return useQuery({
    queryKey: ['messages', params],
    queryFn: () => messagingApi.listMessages(params),
  });
}

export function useMessage(id: string | undefined) {
  return useQuery({
    queryKey: ['messages', 'detail', id],
    queryFn: () => messagingApi.getMessage(id!),
    enabled: !!id,
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Message>) => messagingApi.sendMessage(data),
    meta: mutationMeta.sent('Message'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['mailbox'] });
    },
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => messagingApi.markAsRead(id),
    meta: mutationMeta.silentSuccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread'] });
    },
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['messages', 'unread'],
    queryFn: () => messagingApi.getUnreadCount(),
    refetchInterval: 15_000,
  });
}

export function useFlaggedCount() {
  return useQuery({
    queryKey: ['messages', 'flagged'],
    queryFn: () => messagingApi.getFlaggedCount(),
    refetchInterval: 60_000,
  });
}

export function usePatientSMSHistory(patientId?: string) {
  return useQuery({
    queryKey: ['messages', 'sms', patientId],
    queryFn: ({ signal }) => messagingApi.listPatientSMS(patientId!, 1, 50, signal),
    enabled: !!patientId,
  });
}

export function useSMSConfiguration() {
  return useQuery({
    queryKey: ['messages', 'sms', 'configuration'],
    queryFn: messagingApi.getSMSConfiguration,
    staleTime: 60_000,
  });
}

export function useSendPatientSMS() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, body }: { patientId: string; body: string }) =>
      messagingApi.sendPatientSMS(patientId, body),
    meta: mutationMeta.sent('SMS'),
    onSuccess: (_message, variables) => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'sms', variables.patientId] });
    },
  });
}

export function useMailbox(params: {
  folder: MailboxFolder;
  page: number;
  per_page: number;
  q?: string;
}) {
  return useQuery({
    queryKey: ['mailbox', params],
    queryFn: ({ signal }) => messagingApi.listMailbox(params, signal),
    placeholderData: (previous, previousQuery) => {
      const previousParams = previousQuery?.queryKey[1] as typeof params | undefined;
      const sameMailbox =
        previousParams?.folder === params.folder &&
        previousParams?.q === params.q &&
        previousParams?.per_page === params.per_page;
      return sameMailbox ? previous : undefined;
    },
  });
}

export function useMailThread(id?: string) {
  return useQuery({
    queryKey: ['mailbox', 'thread', id],
    queryFn: () => messagingApi.getMailThread(id!),
    enabled: !!id,
  });
}

export function useMailDraft(id?: string) {
  return useQuery({
    queryKey: ['mailbox', 'draft', id],
    queryFn: () => messagingApi.getDraft(id!),
    enabled: !!id,
  });
}

export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      id,
      version,
    }: {
      data: MailComposePayload;
      id?: string;
      version?: number;
    }) =>
      id ? messagingApi.updateDraft(id, { ...data, version }) : messagingApi.createDraft(data),
    meta: mutationMeta.silentSuccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailbox'] });
    },
  });
}

export function useDeleteDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: messagingApi.deleteDraft,
    meta: mutationMeta.silentSuccess,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailbox'] }),
  });
}

export function useSendMailboxEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      files,
      onProgress,
    }: {
      data: MailComposePayload;
      files?: File[];
      onProgress?: (percent: number) => void;
    }) => messagingApi.sendMailboxEmail(data, files, onProgress),
    meta: mutationMeta.sent('Email'),
    onSuccess: (thread) => {
      queryClient.invalidateQueries({ queryKey: ['mailbox'] });
      queryClient.setQueryData(['mailbox', 'thread', thread.id], thread);
    },
  });
}

export function useUpdateMailboxThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: Parameters<typeof messagingApi.updateMailboxThread>[1];
    }) => messagingApi.updateMailboxThread(id, action),
    meta: mutationMeta.silentSuccess,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailbox'] }),
  });
}

export function useBulkUpdateMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      action,
    }: {
      ids: string[];
      action: Parameters<typeof messagingApi.bulkUpdateMailbox>[1];
    }) => messagingApi.bulkUpdateMailbox(ids, action),
    meta: mutationMeta.silentSuccess,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailbox'] }),
  });
}
